(() => {
  const GLOBAL_FLAG = "__ugxPortableFaqInitialized";
  const SECTION_SELECTOR = "[data-ugx-faq-search-section]";
  const INPUT_SELECTOR = "[data-ugx-faq-search-input]";
  const CLEAR_SELECTOR = "[data-ugx-faq-search-clear]";
  const GROUP_SELECTOR = "[data-ugx-faq-group]";
  const ITEM_SELECTOR = "[data-ugx-faq-item]";
  const REVEAL_SELECTOR = ".ugx-faq__reveal";

  const normalize = (value) => {
    return String(value || "")
      .normalize("NFKC")
      .toLocaleLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  };

  const getTerms = (value) => normalize(value).split(" ").filter(Boolean);

  const includesAnyTerm = (haystack, terms) => {
    const normalizedHaystack = normalize(haystack);
    return terms.some((term) => normalizedHaystack.includes(term));
  };

  const saveInitialOpenState = (section) => {
    section.querySelectorAll("details").forEach((details) => {
      if (!details.hasAttribute("data-ugx-initial-open")) {
        details.setAttribute("data-ugx-initial-open", details.open ? "true" : "false");
      }
    });
  };

  const restoreInitialOpenState = (section) => {
    section.querySelectorAll("details").forEach((details) => {
      details.open = details.getAttribute("data-ugx-initial-open") === "true";
    });
  };

  const initReveal = (section) => {
    const revealItems = section.querySelectorAll(REVEAL_SELECTOR);
    if (!revealItems.length) return;

    if (!section.classList.contains("ugx-faq--reveal-enabled")) {
      revealItems.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      revealItems.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    revealItems.forEach((item) => observer.observe(item));
  };

  const initSection = (section) => {
    if (!section || section.getAttribute("data-ugx-faq-ready") === "true") return;

    const input = section.querySelector(INPUT_SELECTOR);
    const clearButton = section.querySelector(CLEAR_SELECTOR);
    const emptyMessage = section.querySelector("[data-ugx-faq-empty]");
    const resultCount = section.querySelector("[data-ugx-faq-result-count]");
    const resultCountSuffix = section.getAttribute("data-result-count-suffix") || "results found.";

    saveInitialOpenState(section);
    initReveal(section);

    const setResponsivePlaceholder = () => {
      if (!input) return;
      const isMobile = window.matchMedia("(max-width: 989px)").matches;
      const desktopPlaceholder = input.getAttribute("data-placeholder-desktop") || "";
      const mobilePlaceholder = input.getAttribute("data-placeholder-mobile") || desktopPlaceholder;
      input.setAttribute("placeholder", isMobile ? mobilePlaceholder : desktopPlaceholder);
    };

    const resetSearch = () => {
      section.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
        group.hidden = false;
      });

      section.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
        item.hidden = false;
      });

      restoreInitialOpenState(section);

      if (emptyMessage) emptyMessage.hidden = true;
      if (resultCount) resultCount.textContent = "";
      if (clearButton) clearButton.hidden = true;
    };

    const applySearch = () => {
      if (!input) return;

      const terms = getTerms(input.value);
      if (!terms.length) {
        resetSearch();
        return;
      }

      let visibleCount = 0;

      section.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
        const groupText = group.getAttribute("data-ugx-group-search") || "";
        const groupMatched = includesAnyTerm(groupText, terms);
        let groupVisibleCount = 0;

        group.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
          const itemText = item.getAttribute("data-ugx-item-search") || "";
          const itemMatched = groupMatched || includesAnyTerm(itemText, terms);

          item.hidden = !itemMatched;

          if (itemMatched) {
            groupVisibleCount += 1;
            visibleCount += 1;

            const details = item.querySelector("details");
            if (details) details.open = true;
          }
        });

        group.hidden = groupVisibleCount === 0;
      });

      if (emptyMessage) emptyMessage.hidden = visibleCount > 0;
      if (clearButton) clearButton.hidden = false;
      if (resultCount) {
        resultCount.textContent = visibleCount > 0 ? `${visibleCount} ${resultCountSuffix}` : "";
      }
    };

    if (input) {
      input.addEventListener("input", applySearch);
      setResponsivePlaceholder();

      const mediaQuery = window.matchMedia("(max-width: 989px)");
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", setResponsivePlaceholder);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(setResponsivePlaceholder);
      }
    }

    if (clearButton && input) {
      clearButton.addEventListener("click", () => {
        input.value = "";
        input.focus();
        resetSearch();
      });
    }

    section.setAttribute("data-ugx-faq-ready", "true");
  };

  const initAll = (scope = document) => {
    if (!scope) return;

    if (scope.matches && scope.matches(SECTION_SELECTOR)) {
      initSection(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    }
  };

  if (!window[GLOBAL_FLAG]) {
    window[GLOBAL_FLAG] = true;
    window.UGXPortableFaqInit = initAll;

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => initAll(document), { once: true });
    } else {
      initAll(document);
    }

    document.addEventListener("shopify:section:load", (event) => {
      if (event && event.target) initAll(event.target);
    });
  } else if (typeof window.UGXPortableFaqInit === "function") {
    window.UGXPortableFaqInit(document);
  }
})();
